<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import UserResource from '@/resources/user'
import CalendarResource from '@/resources/calendar'
import ReservationCheckoutResource from '@/resources/reservationCheckout'
import { createCountdown } from '@/util/timers'
import ApexClubPitch from '../components/ApexClubPitch.vue'
import ApexClubDetail from '../components/ApexClubDetail.vue'

export default {
    components: {
        ApexClubPitch,
        ApexClubDetail
    },
    computed: {
        ...mapGetters(['userResource', 'isApexClub']),
        calendarId() {
            return this.$route.params.calendarId
        },
        upcomingReservationResources() {
            if (!this.upcomingReservationsPageData) {
                return []
            }

            return this.upcomingReservationsPageData.resources
        },
        seatedUserID() {
            return this.seated?.current?.user
        },
        seatedCanProceed() {
            if (this.seatedCanProceedOverride) {
                return true
            }

            return this.seatedUserID == this.userResource.id
        },
        userHasEarlyEntry() {
            if (this.seated?.next?.user != this.userResource.id) {
                return false
            }

            const earlyEntranceDate = (this.seated.next?.user == this.userResource.id) ? moment(this.seated.next?.time).subtract(20, 'minutes') : null
            return earlyEntranceDate && !this.seated.current && this.isApexClub && (moment() > earlyEntranceDate)
        }
    },
    data() {
        return {
            loading: true,
            calendarResource: null,
            checkoutResource: null,
            upcomingReservationsPageData: null,
            seatedCanProceedOverride: false,
            seated: null,
            intervalIDSeated: null,
            countdown: null,
            statusCodes: {
                locked: {
                    text: 'This simulator is locked. Click "Book Now" to unlock',
                    variant: 'danger'
                },
                inUse: {
                    text: 'Someone else is using this simulator. Click "Book Now" to reserve',
                    variant: 'danger'
                },
                partyDisbanded: {
                    text: 'Party has been disbanded',
                    variant: 'info'
                },
            }
        }
    },
    methods: {
        async load() {
            this.calendarResource = await CalendarResource.detail(this.calendarId, { resolveRelated: true })
            this.upcomingReservationsPageData = await UserResource.getReservationsFuture()
            const session = this.$route.query.session || null

            if (this.$route.query.checkout) {
                this.checkoutResource = await ReservationCheckoutResource.detail(this.$route.query.checkout)
            }

            if (this.$route.query.statusCode) {
                if (this.$route.query.statusCode == 'paymentLinkSuccess') {
                    const newQuery = { ...this.$route.query }
                    delete newQuery.statusCode
                    delete newQuery.checkout
                    const value = (this.checkoutResource?.attributes.checkout_data.total_cents) ? this.checkoutResource.attributes.checkout_data.total_cents / 100 : null
                    this.$analytics.track('checkout_success', { value, session, method: 'payment_link', currency: 'USD' })
                    this.$router.replace({ query: newQuery })
                } else {
                    const message = this.statusCodes[this.$route.query.statusCode]
                    this.addMessage(message.variant || 'danger', message.text || 'An unknown error occured')
                }
            }

            await this.fetchSeated()
            this.intervalIDSeated = setInterval(() => this.fetchSeated(), 1e4)
            this.loading = false
        },
        async fetchSeated() {
            this.seated = await this.calendarResource.seated()
            console.log(this.seated)

            if (this.seated?.next?.time && this.seated?.next?.user == this.userResource.id) {
                const timer = createCountdown(this.seated.next.time)

                const countdown = {
                    hours: null,
                    mins: null,
                    secs: null,
                    stop: timer.stop
                }

                timer.emitter.on('tick', (payload) => {
                    if (!payload) {
                        this.countdown = null
                        timer.stop()
                        return null
                    }

                    countdown.hours = payload.hours
                    countdown.mins = payload.mins
                    countdown.secs = payload.secs
                })

                this.countdown = countdown
                timer.start()
            } else {
                this.countdown = null
            }
        },
        onApexClubRegister() {
            this.$refs.apexClubModal.hide()
        }
    },
    async mounted() {
        await this.load()
    },
    beforeDestroy() {
        if (this.intervalIDSeated) {
            clearInterval(this.intervalIDSeated)
        }

        if (this.countdown) {
            this.countdown.stop()
        }
    }
}
</script>

<template>
    <div>
        <b-container v-if="!loading">
            <b-row class="mt-3">
                <b-col md="8" offset-md="2">
                    <messages />
                    
                    <b-card v-if="seatedCanProceed" class="my-3">
                        <h4 class="text-center">"{{ calendarResource }}" ist bereit für Sie!</h4>
                        <p class="text-center">Bitte gehen Sie hinüber und setzen Sie sich</p>
                        <hr />
                        <p class="text-center text-muted">Konfigurieren Sie Ihr Rennen auf dem Startbildschirm oder indem Sie unten auf "Los geht's" klicken.</p>
                        <p class="text-center"><b-button :to="{ name: 'calendarControl', params: { calendarId: $route.params.calendarId }, query: { loginUser: userResource.id } }" variant="secondary">Los geht's!</b-button></p>
                    </b-card>

                    <b-card v-else-if="countdown">
                        <p class="text-center">Ihre nächste Sitzung beginnt in</p>
                        <div class="hms text-center">
                            {{ countdown.hours }}:{{ countdown.mins }}:{{ countdown.secs }}
                        </div>
                        <div v-if="isApexClub && seated.next && seated.next.user == userResource.id">
                            <div class="text-center mb-2">
                                <b-badge variant="success"><b-icon icon="check" /> {{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }}</b-badge>
                            </div>
                            <p class="text-center text-muted">Sie sind dabei! Sie erhalten frühzeitigen Zugang!</p>
                            <b-button @click="seatedCanProceedOverride = true" :disabled="!userHasEarlyEntry" class="col-sm-12">
                                Lassen Sie mich früh rein!
                            </b-button>
                        </div>
                        <div v-else class="text-center">
                            <hr />
                            <p><strong>{{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }} Mitglieder können Sims frühzeitig freischalten!</strong> {{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }} Mitglieder sparen auch 20% bei all ihren Rennen und erhalten Zugang zu exklusiven Rennen!</p>
                            <b-button @click="$refs.apexClubModal.show()">Apex Club beitreten</b-button>
                        </div>
                    </b-card>

                    <div v-else>
                        <p class="text-muted text-center">Kehren Sie innerhalb einer Stunde vor Ihrer nächsten Reservierung auf diesem Simulator ("{{ calendarResource }}") hierher zurück.</p>
                        <b-card header="Bevorstehende Reservierungen">
                            <b-table-simple v-if="upcomingReservationResources.length > 0">
                                <b-tr>
                                    <b-th>Datum</b-th>
                                    <b-th>Simulator</b-th>
                                    <b-th>&nbsp;</b-th>
                                </b-tr>

                                <b-tr v-for="(reserv, i) in upcomingReservationResources" :key="i">
                                    <b-td>{{ [reserv.getDateFormatted(), reserv.getTimeFormatted()].join(' @ ') }}</b-td>
                                    <b-td>{{ reserv.attributes.calendar_name }}</b-td>
                                    <b-td><a :href="reserv.getAddToCalendarLink()"><b-icon icon="calendar-plus" /> Zum Kalender hinzufügen</a></b-td>
                                </b-tr>
                            </b-table-simple>

                            <p class="text-center text-muted" v-else>
                                Keine bevorstehenden Reservierungen <br />
                                <b-button variant="secondary" :to="{ name: 'reservationsSingle', params: { calendarId } }" class="mt-3">Jetzt buchen!</b-button>
                            </p>
                        </b-card>
                    </div>

                    <b-card class="mt-3 text-center">
                        <p>Machen Sie sich rennbereit auf Ihre Weise. <strong>Passen Sie Ihr Auto, Ihre Strecke, Ihre Hilfen und Ihre Sitzungseinstellungen an</strong>, bevor Sie auf die Strecke gehen. Egal, ob Sie eine entspannte Fahrt oder eine wettbewerbsorientierte Herausforderung wünschen, passen Sie Ihr Erlebnis jederzeit vor dem Rennbeginn an.</p>
                        <b-button variant="primary" :to="{ name: 'racesPresets' }"><b-icon icon="collection-play" /> Passen Sie Ihr Rennen an</b-button>
                    </b-card>
                </b-col>
            </b-row>
        </b-container>

        <b-modal ref="apexClubModal" :title="$appConfig('APEX_CLUB_NAME', 'Apex Club') + ' Anmeldung'" size="md" hide-footer no-fade centered>
            <apex-club-pitch @success="onApexClubRegister">
                <p class="text-muted"><small>(Ja, Sie können Ihre Sitzungen für Freunde und Familie nutzen!) — <a href="javascript:{}" v-b-toggle.collapse-faq>Mehr Infos &amp; FAQ</a></small></p>
                <b-collapse id="collapse-faq" class="mb-3">
                    <b-card>
                        <apex-club-detail />
                    </b-card>
                </b-collapse>
            </apex-club-pitch>
        </b-modal>
    </div>
</template>

<style lang="scss" scoped>
.hms {
    font-size: 60px;
}
</style>
